import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/Common/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { HeroSection } from './components/ui/hero-section'; // Keep this import
import { Dashboard } from './components/Dashboard/Dashboard';
import { EmailConfirmation } from './components/Auth/EmailConfirmation';
import { AuthForm } from './components/Auth/AuthForm';
import { SimpleLoginPage } from './components/Auth/SimpleLoginPage';
import { PasswordReset } from './components/Auth/PasswordReset';
import { ListingTemplatesPage } from './components/ListingTemplates/ListingTemplatesPage';
import { NewListingTemplatePage } from './components/ListingTemplates/NewListingTemplatePage';
import { ViewListingTemplatePage } from './components/ListingTemplates/ViewListingTemplatePage';
import { QuickAuthTest } from './components/Auth/QuickAuthTest';

// Vite compile-time globals provided by vite.config.ts
declare const __BUILD_VERSION__: string;
declare const __BUILD_TIME__: string;

// Prevent redirect loops
let redirectCount = 0;
const MAX_REDIRECTS = 5;
const REDIRECT_RESET_TIME = 10000; // 10 seconds
let lastRedirectTime = 0;

const canRedirect = (_to: string) => {
  const now = Date.now();
  
  // Reset counter after cooldown period
  if (now - lastRedirectTime > REDIRECT_RESET_TIME) {
    redirectCount = 0;
  }
  
  if (redirectCount >= MAX_REDIRECTS) {
    return false;
  }
  
  redirectCount++;
  lastRedirectTime = now;
  return true;
};

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading PromptOS...</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">This should only take a moment</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (canRedirect('/auth')) {
      return <Navigate to="/auth" replace />;
    } else {
      // Show error instead of infinite redirects
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md">
            <h2 className="text-xl font-semibold text-red-600 mb-4">Authentication Loop Detected</h2>
            <p className="text-gray-600 mb-6">Please refresh the page to continue.</p>
            <button
              onClick={() => window.location.href = '/auth'}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg"
            >
              Go to Login
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { user, login, signup, resetPassword, confirmEmail: _confirmEmail, isLoading: _isLoading } = useAuth();

  // Add error boundary for routing
  const [routingError, setRoutingError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Reset routing error when location changes
    setRoutingError(null);
  }, [window.location.pathname]);

  // If there's a routing error, show fallback
  if (routingError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4">
            Navigation Error
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {routingError}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  try {
    return (
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HeroSection />} />
        <Route 
          path="/login" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <SimpleLoginPage onLogin={login} />
            )
          } 
        />
        <Route 
          path="/auth" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <AuthForm 
                onLogin={login}
                onSignup={signup}
                onResetPassword={resetPassword}
              />
            )
          } 
        />
        <Route 
          path="/confirm" 
          element={
            <EmailConfirmation 
              onBackToLogin={() => window.location.href = '/auth'} 
            />
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            <PasswordReset 
              onBackToLogin={() => window.location.href = '/auth'} 
            />
          } 
        />
        <Route 
          path="/test-auth" 
          element={<QuickAuthTest />} 
        />

        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <AuthGuard>
              <Dashboard />
            </AuthGuard>
          } 
        />
        <Route 
          path="/listing-templates" 
          element={
            <AuthGuard>
              <ListingTemplatesPage />
            </AuthGuard>
          } 
        />
        <Route 
          path="/listing-templates/new" 
          element={
            <AuthGuard>
              <NewListingTemplatePage />
            </AuthGuard>
          } 
        />
        <Route 
          path="/listing-templates/:id" 
          element={
            <AuthGuard>
              <ViewListingTemplatePage />
            </AuthGuard>
          } 
        />

        {/* Catch all route */}
        <Route 
          path="*" 
          element={
            canRedirect('/dashboard') ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center p-8">
                  <h2 className="text-xl font-semibold mb-4">Page not found</h2>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            )
          } 
        />
      </Routes>
    );
  } catch (error) {
    console.error('🔴 Routing error:', error);
    setRoutingError(error instanceof Error ? error.message : 'Unknown routing error');
    return null;
  }
};

const App: React.FC = () => {
  const [appError, setAppError] = React.useState<string | null>(null);

  // Debug API key immediately on app load
  useEffect(() => {
    console.log('🔍 APP STARTUP - API Key Debug:');
    console.log('   VITE_OPENAI_API_KEY exists:', !!import.meta.env['VITE_OPENAI_API_KEY']);
    console.log('   Key length:', import.meta.env['VITE_OPENAI_API_KEY']?.length || 0);
    console.log('   Starts with sk-:', import.meta.env['VITE_OPENAI_API_KEY']?.startsWith('sk-') || false);
    console.log('   All VITE env vars:', Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')));

    // Cache version management for seamless updates - delayed to avoid SSL conflicts
    const checkCacheVersion = async () => {
      // Wait 5 seconds after app load to avoid SSL handshake conflicts (increased for Chromium)
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      try {
        // Use compile-time constants injected by Vite
        const currentVersion = `${__BUILD_VERSION__}|${__BUILD_TIME__}`;

        const storedVersion = localStorage.getItem('app_cache_version');
        if (storedVersion && storedVersion !== currentVersion) {
          console.log('🔄 App version changed, clearing cache and refreshing');
          // Clear various cache types
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => caches.delete(name));
            });
          }
          // Broadcast new version so other tabs refresh, then reload this tab
          localStorage.setItem('app_cache_version', currentVersion);
          setTimeout(() => window.location.reload(), 200);
          return;
        }
        if (!storedVersion) {
          localStorage.setItem('app_cache_version', currentVersion);
        }
      } catch (error) {
        console.warn('⚠️ Cache version check failed:', error);
      }
    };

    checkCacheVersion();
  }, []);

  // Add error logging
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      
      // If it's a critical error that would cause blank screen, show fallback
      if (event.error?.message?.includes('ChunkLoadError') || 
          event.error?.message?.includes('Loading chunk') ||
          event.error?.message?.includes('Failed to fetch dynamically imported module')) {
        setAppError('App update detected. Please refresh the page to load the latest version.');
      }
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Handle auth-related promise rejections
      const reason = event.reason?.message || String(event.reason);
      if (reason.includes('refresh_token') || reason.includes('Invalid Refresh Token')) {
        console.log('🔄 AUTH: Clearing auth data due to unhandled promise rejection - NO RELOAD');
        // Don't show error for auth issues, just clear data
        import('./lib/supabase').then(({ clearAllAuthData }) => {
          clearAllAuthData().catch(console.warn);
        });
        // Prevent default to stop any potential reload
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Reload stale tabs when another tab updates to a new build version
  React.useEffect(() => {
    const currentVersion = `${__BUILD_VERSION__}|${__BUILD_TIME__}`;
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'app_cache_version' && e.newValue && e.newValue !== currentVersion) {
        console.log('🔄 Detected new app version in another tab. Reloading...');
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Show app error fallback if needed
  if (appError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        <div className="text-center p-8 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-md">
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          
          <h2 className="text-xl font-semibold text-white mb-4">
            PromptOS Update Available
          </h2>
          
          <p className="text-white/80 mb-6">
            {appError}
          </p>
          
          <button
            onClick={() => {
              console.log('🔄 Manual refresh requested by user');
              window.location.reload();
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-all duration-300 font-semibold"
          >
            🔄 Refresh PromptOS
          </button>
        </div>
      </div>
    );
  }

  // Add browser compatibility checks
  React.useEffect(() => {
    // Check for required browser features
    const requiredFeatures = [
      'fetch',
      'Promise', 
      'localStorage',
      'sessionStorage'
    ];
    
    const missingFeatures = requiredFeatures.filter(feature => !(feature in window));
    
    if (missingFeatures.length > 0) {
      console.warn('⚠️ Missing browser features:', missingFeatures);
      setAppError(`Your browser is missing required features: ${missingFeatures.join(', ')}. Please update your browser.`);
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;